import { baseArkhaleAPI } from './base-api'
import { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import {
  AggregatedTimeseriesData,
  LatestTimeseriesDataWithPrevious,
  TimeseriesData,
} from '../../shared/models/telemetry/telemetry.model'
import { AggregationType } from '../../shared/models/time.model'
import { EntityId } from '../../shared/models/id/entity-id'

export enum QueryType {
  LATEST = 'LATEST',
  REAL_TIME = 'REAL_TIME',
  HISTORY = 'HISTORY',
}

export interface BaseTimeseriesCmd<T extends QueryType> {
  id: string
  type: T
  useStorageTsV2?: boolean
}

export interface LatestTimeseriesCmd
  extends BaseTimeseriesCmd<QueryType.LATEST> {
  entityID: EntityId
  keys: string[]
}

export const isLatestTimeseriesCmd = (cmd: any): cmd is LatestTimeseriesCmd => {
  return cmd && cmd.type === QueryType.LATEST
}

export interface TimeseriesCmd extends BaseTimeseriesCmd<QueryType.REAL_TIME> {
  entityID: EntityId
  keys: string[]
  startTs?: number
  timeWindowMs: number
  aggregationType: AggregationType
  aggregationIntervalMs: number
  limit?: number
  order?: 'ASC' | 'DESC'
}

export const isTimeseriesCmd = (cmd: any): cmd is TimeseriesCmd => {
  return cmd && cmd.type === QueryType.REAL_TIME
}

export interface TimeseriesHistoryCmd
  extends BaseTimeseriesCmd<QueryType.HISTORY> {
  entityID: EntityId
  keys: string[]
  startTs: number
  endTs: number
  aggregationType: AggregationType
  aggregationIntervalMs: number
  limit?: number
  order?: 'ASC' | 'DESC'
}

export const isTimeseriesHistoryCmd = (
  cmd: any
): cmd is TimeseriesHistoryCmd => {
  return cmd && cmd.type === QueryType.HISTORY && cmd.startTs && cmd.endTs
}

export interface TimeseriesAggCmd
  extends BaseTimeseriesCmd<QueryType.REAL_TIME> {
  entityID: EntityId
  keys: AggKey[]
  startTs?: number
  timeWindowMs: number
  aggregated: boolean
}

export function isTimeseriesAggCmd(cmd: any): cmd is TimeseriesAggCmd {
  return (
    cmd &&
    cmd.type === QueryType.REAL_TIME &&
    cmd.keys &&
    cmd.startTs &&
    cmd.timeWindowMs &&
    cmd.aggregated
  )
}

export interface TimeseriesHistoryAggCmd
  extends BaseTimeseriesCmd<QueryType.HISTORY> {
  entityID: EntityId
  keys: AggKey[]
  startTs: number
  endTs: number
  aggregated: boolean
}

export function isTimeseriesHistoryAggCmd(
  cmd: any
): cmd is TimeseriesHistoryAggCmd {
  return (
    cmd &&
    cmd.type === QueryType.HISTORY &&
    cmd.keys &&
    cmd.startTs &&
    cmd.endTs &&
    cmd.aggregated
  )
}

export interface AggKey {
  key: string
  aggregationType: AggregationType
  previousStartTs?: number
  previousEndTs?: number
  previousValueOnly?: boolean
}

const timeSeriesAPI = baseArkhaleAPI.injectEndpoints({
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    getTimeSeries: build.query<
      TimeseriesData,
      TimeseriesCmd | TimeseriesHistoryCmd
    >({
      query: (
        args: TimeseriesCmd | TimeseriesHistoryCmd
      ): string | FetchArgs => ({
        url: `telemetries/${args.entityID.entityType}/${args.entityID.id}/values/timeseries`,
        method: 'GET',
        params: {
          type: args.type,
          keys: args.keys,
          startTs: args.startTs,
          timeWindowMs: isTimeseriesCmd(args) ? args.timeWindowMs : undefined,
          endTs: isTimeseriesHistoryCmd(args) ? args.endTs : undefined,
          aggregationType: args.aggregationType,
          aggregationIntervalMs: args.aggregationIntervalMs,
          limit: args.limit,
          order: args.order,
          useStorageTsV2: args.useStorageTsV2,
        },
      }),
    }),
    getLatestTimeSeries: build.query<
      LatestTimeseriesDataWithPrevious,
      LatestTimeseriesCmd
    >({
      query: (args: LatestTimeseriesCmd): string | FetchArgs => ({
        url: `telemetries/${args.entityID.entityType}/${args.entityID.id}/values/timeseries/latest`,
        method: 'GET',
        params: {
          keys: args.keys,
          useStorageTsV2: args.useStorageTsV2,
        },
      }),
    }),
    getAggTimeSeries: build.query<
      AggregatedTimeseriesData,
      TimeseriesAggCmd | TimeseriesHistoryAggCmd
    >({
      query: (
        args: TimeseriesAggCmd | TimeseriesHistoryAggCmd
      ): string | FetchArgs => ({
        url: `telemetries/${args.entityID.entityType}/${args.entityID.id}/values/timeseries/aggregate`,
        method: 'POST',
        params: {
          type: args.type,
          useStorageTsV2: args.useStorageTsV2,
        },
        body: {
          type: args.type,
          keys: args.keys,
          startTs: args.startTs,
          timeWindowMs: isTimeseriesAggCmd(args)
            ? args.timeWindowMs
            : undefined,
          endTs: isTimeseriesHistoryAggCmd(args) ? args.endTs : undefined,
        },
      }),
    }),
  }),
})

export const {
  useLazyGetTimeSeriesQuery,
  useLazyGetLatestTimeSeriesQuery,
  useLazyGetAggTimeSeriesQuery,
} = timeSeriesAPI
